type TLastSeenInput =
  | Date
  | string
  | {toDate(): Date}
  | {seconds: number; nanoseconds?: number}
  | null
  | undefined;

/**
 * Normalises Firestore timestamps, strings, and dates into a JavaScript Date.
 */
function lastSeenInputToDate(dateInput: TLastSeenInput): Date | null {
  if (!dateInput) {
    return null;
  }

  if (dateInput instanceof Date) {
    return dateInput;
  }

  if (typeof dateInput === 'string') {
    const parsed = new Date(dateInput);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if ('toDate' in dateInput && typeof dateInput.toDate === 'function') {
    const parsed = dateInput.toDate();
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if ('seconds' in dateInput) {
    const parsed = new Date(dateInput.seconds * 1000 + (dateInput.nanoseconds ?? 0) / 1e6);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

/**
 * Converts a last-seen timestamp into a relative day label (e.g. "3 days ago").
 */
export function formatLastSeenDaysAgo(dateInput: TLastSeenInput): string {
  const date = lastSeenInputToDate(dateInput);
  if (!date) {
    return '';
  }
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((todayMidnight.getTime() - dateMidnight.getTime()) / (24 * 60 * 60 * 1000));

  if (diffDays <= 0) {
    return 'today';
  }
  if (diffDays === 1) {
    return '1 day ago';
  }
  return `${diffDays} days ago`;
}
